import React, { useState, useEffect, useContext } from "react";
// import DxCompany from '../../stacks/company/DxCompanyMock'
import Button from "../Button/Button";
import CheckProgress from "../CheckProgress/CheckProgress";
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";
import DxCompany from "../../stacks/company/DxCompanyAWS";
import Layout from "../Layout/Layout";
import PageHeader from "../PageHeader/PageHeader";
import PageTransition from "../PageTransition/PageTransition";
import Progress from "../../stacks/progress/ProgressAWS";
import SelectNAICSModal from "../SelectNAICSModal/SelectNAICSModal";
import styled from "styled-components";
import { Block, FadeCol } from "../../styles/commons";
import { ProgressesStatus } from "../../context";
import { companyHeadRoles } from "../../data/companyHeadRoles";
import { companyTypes } from "../../data/companyTypes";
import { usStates } from "../../data/usStates";
import { useErrorToast } from "../../hooks/useErrorToast";
import { useFormValidation } from "../../hooks/useFormValidation";
import {
  Container,
  Row,
  Col,
  Form,
  Label,
  Input,
  FormGroup,
  FormText,
} from "reactstrap";

const CompanyScreen = (props) => {
  const [isDirty, setIsDirty] = useState(false)
  const [showNAICSModel, setShowNAICSModal] = useState(false);
  const [showNAICS2Model, setShowNAICS2Modal] = useState(false);
  const [acceptableNaics, setAcceptableNaics] = useState();
  const [customType, setCustomType] = useState("");
  const [customRole, setCustomRole] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({});
  const [fieldSet] = useState({
    companyName: "",
    companyType: "",
    companyRole: "",
    companyAddress1: "",
    companyAddress2: "",
    companyCity: "",
    companyState: "",
    companyZip: "",
    companyPhone1: "",
    companyURL: "",
    companyNAICS: "",
  });

  const { setErrorMsg, errorToast } = useErrorToast("");
  const { validated, setValidated, checkValidation } = useFormValidation(form, [
    "companyName",
    "companyAddress1",
    "companyCity",
    "companyState",
    "companyZip",
    "companyNAICS",
    "companyRole",
    "companyType",
  ]);

  const otherLegalEntityType = "Other Legal Entity Description";
  const otherExecutiveRole = "Other Detailed Description";

  const progress = useContext(ProgressesStatus);

  useEffect(() => {
    setErrorMsg(error);
  }, [error, setErrorMsg]);

  useEffect(() => {
    async function getProgess() {
      const progress = await CheckProgress("company");
      if (progress === false) {
        props.history.push("/progress");
      }
    }
    props.product === 'ica' && getProgess();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  const api = DxCompany.getInstance();
  const progressApi = Progress.getInstance();

  useEffect(() => {
    // ensure all fields are in the form
    for (const field in fieldSet) {
      if (form[field] === undefined) {
        form[field] = "";
      }
    }
  }, [form, fieldSet]);

  useEffect(() => {
    const api = DxCompany.getInstance();
    Promise.all([api.retrieve(), api.retrieveAcceptableNaics()])
      .then((values) => {
        const company = values[0];
        company.status && setError(JSON.stringify(company, null, 2));
        if (
          company.companyType !== undefined &&
          !companyTypes.includes(company.companyType)
        ) {
          setCustomType(company.companyType);
          company.companyType = otherLegalEntityType;
        }
        if (
          company.companyRole !== undefined &&
          !companyHeadRoles.includes(company.companyRole)
        ) {
          setCustomRole(company.companyRole);
          company.companyRole = otherExecutiveRole;
        }

        setForm(company);

        const naics = values[1];
        naics.status && setError(JSON.stringify(naics, null, 2));
        setAcceptableNaics(naics);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
        console.log(`Company/acceptableNaics retreive failed ${err.message}`);
      });
  }, []);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    setIsDirty(true)
  }

  function selectNAICSCode(code) {
    setForm({ ...form, companyNAICS: code });
    setIsDirty(true)
  }

  function selectNAICS2Code(code) {
    setForm({ ...form, companyNAICS2: code });
    setIsDirty(true)
  }

  async function storeCompany() {
    const isValid = checkValidation();
    setValidated(isValid);
    if (!isValid) {
      setErrorMsg(`Incomplete data marked in red`);
      return false;
    }
    form.companyNAICS = String(form.companyNAICS);
    if (form.companyNAICS2) {
      form.companyNAICS2 = String(form.companyNAICS2);
    }
    try {
      setIsDirty(false)
      const res = await api.store({
        ...form,
        companyType:
          form.companyType === otherLegalEntityType
            ? customType
            : form.companyType,
        companyRole:
          form.companyRole === otherExecutiveRole
            ? customRole
            : form.companyRole,
      });
      if (![200, 201].includes(res.status)) {
        setErrorMsg(JSON.stringify(res, null, 2));
        setIsDirty(true)
        return false;
      }
      props.product === 'ica' && props.history.push("/progress")
      return true;
    } catch (err) {
      setErrorMsg(`Store error: ${JSON.stringify(err, null, 2)}`);
      console.error(`Store error: ${JSON.stringify(err, null, 2)}`);
      setIsDirty(true)
      return false;
    }
  }

  async function complete() {
    try {
      const data = await progressApi.store({ company: "completed" });
      progress.update(data.progresses);
      props.history.push("/progress");
    } catch (err) {
      setErrorMsg(`Company complete error: ${JSON.stringify(err, null, 2)}`);
      console.error(`Company complete error: ${JSON.stringify(err, null, 2)}`);
    }
  }

  async function store() {
    const sent = await storeCompany();
    if (props.product === 'ica' && sent) {
      await complete();
    }
  }
  function formatTel({ target: { name, value } }) {
    let tel = value;
    if (value.length === 4) {
      if (value[3] === "-") {
        tel = value.slice(0, 3);
      } else {
        tel = [value.slice(0, 3), "-", value.slice(3)].join("");
      }
    }

    if (value.length === 8) {
      if (value[7] === "-") {
        tel = value.slice(0, 7);
      } else {
        tel = [value.slice(0, 7), "-", value.slice(7)].join("");
      }
    }

    if (value.length <= 12) {
      setForm({ ...form, [name]: tel });
      setIsDirty(true)
    }
  }

  return (
    <Layout product={props.product}>
      <PageHeader title="Company">
        {/* <Button
          small
          color='#28b536'
          click={() => complete()}
        >
          COMPLETE
        </Button> */}
      </PageHeader>
      <PageTransition loading={loading} msg="Loading company information...">
        <Container>
          <Row>
            <Col>
              <p>{`Please provide the information about your company.`}</p>
            </Col>
          </Row>
          <Block>
            <FormText>
              * <i>required field</i>
            </FormText>
            <div style={{ height: "40px" }} />
            <Form>
              <Row form>
                <Col lg={12}>
                  <FormGroup>
                    <Label>Company name*</Label>
                    <Input
                      type="text"
                      value={form.companyName}
                      name="companyName"
                      data-testid="companyName"
                      onChange={handleChange}
                      invalid={!validated && !form.companyName}
                    />
                  </FormGroup>
                </Col>
              </Row>
              <Row form>
                <Col md={6}>
                  <FormGroup>
                    <Label>Role of Key Executive*</Label>
                    <Input
                      type="select"
                      value={form.companyRole}
                      name="companyRole"
                      data-testid="companyRole"
                      onChange={handleChange}
                      invalid={!validated && !form.companyRole}
                    >
                      <option key={""} value="">
                        Select role
                      </option>
                      {companyHeadRoles.map((role) => (
                        <option key={`type_${role}`} value={role}>
                          {role}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                {form.companyRole === otherExecutiveRole && (
                  <FadeCol md={6}>
                    <FormGroup>
                      <Label>Custom role</Label>
                      <Input
                        type="text"
                        value={customRole}
                        data-testid="customRole"
                        onChange={(e) => {setCustomRole(e.target.value); setIsDirty()}}
                      />
                    </FormGroup>
                  </FadeCol>
                )}
              </Row>
              <Row form>
                <Col md={6}>
                  <FormGroup>
                    <Label>Company Legal Entity Type*</Label>
                    <Input
                      type="select"
                      value={form.companyType}
                      name="companyType"
                      data-testid="companyType"
                      onChange={handleChange}
                      invalid={!validated && !form.companyType}
                    >
                      <option value="">Select company type</option>
                      {companyTypes.map((type) => (
                        <option key={`type_${type}`} value={type}>
                          {type}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                {form.companyType === otherLegalEntityType && (
                  <FadeCol md={6}>
                    <FormGroup>
                      <Label>Custom company type</Label>
                      <Input
                        type="text"
                        value={customType}
                        data-testid="customType"
                        onChange={(e) => {setCustomType(e.target.value); setIsDirty()}}
                      />
                    </FormGroup>
                  </FadeCol>
                )}
              </Row>
              <FormGroup>
                <Label>Company Address 1*</Label>
                <Input
                  type="text"
                  value={form.companyAddress1}
                  name="companyAddress1"
                  data-testid="companyAddress1"
                  onChange={handleChange}
                  invalid={!validated && !form.companyAddress1}
                />
              </FormGroup>
              <FormGroup>
                <Label>Company Address 2</Label>
                <Input
                  type="text"
                  value={form.companyAddress2}
                  name="companyAddress2"
                  data-testid="companyAddress2"
                  onChange={handleChange}
                />
              </FormGroup>
              <Row form>
                <Col md={6}>
                  <FormGroup>
                    <Label>City*</Label>
                    <Input
                      type="text"
                      value={form.companyCity}
                      name="companyCity"
                      data-testid="companyCity"
                      onChange={handleChange}
                      invalid={!validated && !form.companyCity}
                    />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label>State*</Label>
                    <Input
                      type="select"
                      value={form.companyState}
                      name="companyState"
                      data-testid="companyState"
                      onChange={handleChange}
                      invalid={!validated && !form.companyState}
                    >
                      <option value="">Select state</option>
                      {usStates.map((state) => (
                        <option
                          key={`state_${state.short}`}
                          value={state.short}
                        >
                          {state.long}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={2}>
                  <FormGroup>
                    <Label>Zip*</Label>
                    <Input
                      type="text"
                      value={form.companyZip}
                      name="companyZip"
                      data-testid="companyZip"
                      onChange={handleChange}
                      invalid={!validated && !form.companyZip}
                    />
                  </FormGroup>
                </Col>
              </Row>
              <Row form>
                <Col md={6}>
                  <FormGroup>
                    <Label>Company Phone 1</Label>
                    <Input
                      type="text"
                      value={form.companyPhone1}
                      name="companyPhone1"
                      data-testid="companyPhone1"
                      onChange={formatTel}
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>Company URL</Label>
                    <Input
                      type="text"
                      value={form.companyURL}
                      name="companyURL"
                      data-testid="companyURL"
                      onChange={handleChange}
                    />
                  </FormGroup>
                </Col>
              </Row>
              <FormGroup>
                <Label>
                  North American Industry Classification System (NAICS) Code*
                </Label>
                <Input
                  type="text"
                  value={form.companyNAICS}
                  name="companyNAICS"
                  data-testid="companyNAICS"
                  onChange={handleChange}
                  readOnly={true}
                  invalid={!validated && !form.companyNAICS}
                />
                <SearchForBtn onClick={() => setShowNAICSModal(true)}>
                  Search for NAICS by sector or keyword
                </SearchForBtn>
              </FormGroup>
              <FormGroup>
                <Label>
                  Secondary North American Industry Classification System (NAICS) Code*
                </Label>
                <Input
                  type="text"
                  value={form.companyNAICS2 || ''}
                  name="companyNAICS2"
                  data-testid="companyNAICS2"
                  onChange={handleChange}
                  readOnly={true}
                  // invalid={!validated && !form.companyNAICS2}
                />
                <SearchForBtn onClick={() => setShowNAICS2Modal(true)}>
                  Search for NAICS by sector or keyword
                </SearchForBtn>
              </FormGroup>
              <hr style={{ marginTop: "40px" }} />
              <div style={{ textAlign: "right" }}>
                {isDirty && <Button click={() => {
                  props.product === 'ica'
                  ? setSaving(true)
                  : store()
                }}>SAVE</Button>
              }
              </div>
            </Form>
          </Block>
          <div style={{ height: 60 }} />
        </Container>
      </PageTransition>
      {showNAICSModel && (
        <SelectNAICSModal
          select={selectNAICSCode}
          close={() => setShowNAICSModal(false)}
          acceptableNaics={acceptableNaics}
        />
      )}
      {showNAICS2Model && (
        <SelectNAICSModal
          select={selectNAICS2Code}
          close={() => setShowNAICS2Modal(false)}
          acceptableNaics={acceptableNaics}
        />
      )}
      {saving && props.product === 'ica' && (
        <ConfirmationModal
          cancel={() => setSaving(false)}
          confirm={store}
          confirmationBtnTitle="SAVE"
        >
          {`Saving locks in the NAICS code for the assessment. Are you sure you want to save? This cannot be undone.`}
        </ConfirmationModal>
      )}
      {errorToast}
    </Layout>
  );
};

const SearchForBtn = styled.p`
  color: var(--primary-accent-color);
  font-size: 14px;
  font-weight: bold;
  margin-top: 5px;
  cursor: pointer;
`;

export default CompanyScreen;
